// React
import { useState, useEffect, useRef, useCallback, type FC } from "react";

// Auth
import { useAuth } from "react-oidc-context";
import { batch } from "react-redux";
import { useOutletContext } from "react-router";

// Styles
import "./TemplateBuilder.css";

// Redux
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  clearMetadata,
  setFilename,
  setFolderName,
  setCategory as setMetadataCategory,
  setAccount,
  setClient,
  setTemplateHtmlById,
  selectTemplateHtmlById,
  selectFilename,
  selectAccountId,
  selectClientId,
  selectFolderName,
  selectAccount,
  selectClient,
} from "../../redux/metadata/metadataSlice";
import type { UserRole } from "../../redux/user/userTypes";
import {
  selectUserRole,
  selectIsSaveDraftDisabledByRole,
} from "../../redux/user/userSelectors";
import {
  selectTemplateVersionsByTemplateId,
  selectVersionIdMapByTemplateId,
  selectAllTemplateFolders,
  selectTemplatesLoading,
  selectTemplatesError,
} from "../../redux/templates/templatesTreeSelectors";
import { templatesActions } from "../../redux/templates/templatesSlice";
import type {
  GetTemplateItem,
  ImportTemplateOperationResponse,
  PublishTemplateOperationResponse,
  TemplateCategory,
} from "../../redux/templates/templatesTypes";
import DEFAULT_HTML from "../../emailTemplates/newsletter-template";

// Local Components
import ActionBar from "../sections/ActionBar";
import PublishBar from "../sections/PublishBar";
import TemplateWorkspace from "../sections/EmailTemplate";
import Editor, { EditorRef } from "../Editor";
import type { EditorImportPayload } from "../Editor";
import { sendTemplateNotification } from "../../features/notifications/utils/sendTemplateNotification";
import { createLogger } from "../../features/notifications/utils/logger";
import { TemplatesDataConnector } from "../../api/templatesDataConnector";
import { S3DataConnector } from "../../api/s3DataConnector";
import { S3_CONSTANTS } from "../../constants/templateConstants";

import { DEFAULT_PUBLISH_VERSION } from "../../types/optionTypes";
import type { PublishOption } from "../../types/optionTypes";

import TemplateLibraryPage from "../Library/pages/TemplateLibraryPage";
import { DeleteModal } from "../modals/Delete";
import { MoveModal } from "../modals/Move";
import { CloneModal } from "../modals/Clone";
import { RenameModal } from "../modals/Rename";
import { SaveAsDraftModal } from "../modals/SaveAsDraft";
import { PublishModal } from "../modals/Publish";
import { ActivateVersionModal } from "../modals/ActivateVersion";
import { HomeConfirmationModal } from "../modals/HomeConfirmation";

import { ArrowDropDownIcon, ArrowDropUpIcon } from "@uhg-harmony/icons-react";
import {
  LoadingIndicator,
  BannerNotification,
  Modal,
  Button,
  Text,
} from "@uhg-harmony/react";
import styled from "styled-components";

// Local Defaults
import { textDefaults } from "../ui/Text";
import formatDateTime from "../utils/formatDateTime";
import {
  mapAPICategoryToUI,
  mapUICategoryToAPI,
} from "../utils/categoryMapping";
import {
  isReadOnlyUser,
  isECPAdmin,
  isClientAdmin,
  isPlatformAdmin,
  getReadOnlyButtonStates,
  canDeactivateVersion,
} from "../utils/roleHelpers";
import { buildRefreshParams } from "./utils/buildRefreshParams";
import { getCachedTemplateHtml, getTemplateHtml } from "./utils/getTemplateHtml";

const ContentWrapper = styled.p`
  flex-wrap: wrap;
  flex-grow: 1;
  justify-content: center;
  padding: 0;
  margin: 0;
`;

const textProps = textDefaults;

const logger = createLogger("TemplateBuilder");

// ==========================================================================
// Module-level pure helpers — no component state dependencies
// ==========================================================================
function normalizeTemplateCategory(
  category?: string,
): TemplateCategory | undefined {
  const normalized = (category || "").trim().toUpperCase();
  if (
    normalized === "OPTUM" ||
    normalized === "PLATFORM" ||
    normalized === "CLIENT"
  ) {
    return normalized;
  }
  return undefined;
}

function canAutoRegisterCategory(
  role: UserRole,
  category: TemplateCategory,
): boolean {
  if (isECPAdmin(role)) return true;
  if (isPlatformAdmin(role))
    return category === "PLATFORM" || category === "CLIENT";
  if (isClientAdmin(role)) return category === "CLIENT";
  return false;
}

function getRoleConflictMessage(
  role: UserRole,
  category: TemplateCategory,
): string {
  if (isReadOnlyUser(role)) {
    return "This template could not be auto-registered. You do not have permission to save templates. Contact your administrator if you need write access.";
  }
  if (
    isClientAdmin(role) &&
    (category === "OPTUM" || category === "PLATFORM")
  ) {
    return "This template could not be auto-registered - your role only permits Client templates. You can still use Save As Draft to save it as a Client template.";
  }
  if (isPlatformAdmin(role) && category === "OPTUM") {
    return "This template could not be auto-registered - your role only permits Platform and Client templates. You can still use Save As Draft to save it under a permitted category.";
  }
  return `This template could not be auto-registered - your role does not permit ${category} templates.`;
}

const TemplateBuilder: FC = () => {
  // ========================================
  // Redux State & Dispatch
  // ========================================
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { resetAccountContext } = useOutletContext<{
    resetAccountContext: () => void;
  }>();
  const userRole = useAppSelector(selectUserRole);
  const isSaveDraftDisabledByRole = useAppSelector(
    selectIsSaveDraftDisabledByRole,
  );
  const platformId = useAppSelector((state) => state.user.platformId);
  const selectedTemplateName = useAppSelector(selectFilename);
  const accountId = useAppSelector(selectAccountId);
  const clientId = useAppSelector(selectClientId);
  const folderName = useAppSelector(selectFolderName);
  const accountName = useAppSelector(selectAccount);
  const clientName = useAppSelector(selectClient);
  const versionsByTemplateId = useAppSelector(
    selectTemplateVersionsByTemplateId,
  );
  const versionIdMap = useAppSelector(selectVersionIdMapByTemplateId);
  const templatesLoading = useAppSelector(selectTemplatesLoading);
  const templatesError = useAppSelector(selectTemplatesError);
  const lastOperation = useAppSelector(
    (state) => state.templates.lastOperation,
  );
  const allTemplateFolders = useAppSelector(selectAllTemplateFolders);
  const presignedUrlByS3Path = useAppSelector(
    (state) => state.templates.presignedUrlByS3Path,
  );
  const templateHtmlById = useAppSelector(selectTemplateHtmlById);

  // ========================================
  // Custom Hooks
  // ========================================
  // Template fetching is now handled by AppLayout on initial load and scope changes.
  // No need to call useGetTemplates here as it would be deduplicated anyway.

  // ========================================
  // Refs
  // ========================================
  const editorRef = useRef<EditorRef>(null);
  const keepLibraryEnabledRef = useRef(false);
  const isLibraryTemplateLoadedRef = useRef(false);
  // Separate abort controller for on-demand presigned-URL fetches so that the
  // selectedPublishVersionValue effect cannot cancel them by sharing the same ref.
  const presignedFetchAbortControllerRef = useRef<AbortController | null>(null);
  // Tracks whether the account/client context effect has run at least once,
  // so we don't reset template state on the very first render.
  const isContextInitializedRef = useRef(false);

  // Holds the templateId of a freshly cloned template so it can be auto-selected
  // once the subsequent getTemplates tree-refresh completes.
  const pendingAutoSelectRef = useRef<string | null>(null);
  const pendingAutoSelectNameRef = useRef<string | null>(null);

  // Holds the new template name submitted via the Rename modal so the success
  // notification can display it (the API only returns a message string).
  const pendingRenameNameRef = useRef<string>("");
  const pendingRenameFolderRef = useRef<string>("");
  const pendingRenameCategoryRef = useRef<TemplateCategory | null>(null);
  const pendingCloneCategoryRef = useRef<TemplateCategory | null>(null);
  // Guards against stale lastOperation when clone/rename fires a second time.
  // Set to false on dispatch; set to true once templatesLoading goes true.
  const cloneLoadingSeenRef = useRef(false);
  const renameLoadingSeenRef = useRef(false);
  const pendingDeleteNameRef = useRef<string>("");
  const pendingDeleteFolderRef = useRef<string>("");
  // Tracks the most recent presigned-url request context so cache read/write
  // uses the exact requested template version.
  const pendingS3RequestRef = useRef<{
    s3Path: string;
    versionValue: string;
    templateId: string;
  } | null>(null);
  // Always holds the current template ID — never cleared by async timeouts.
  // Use this in callbacks to avoid stale closure values from selectedTemplateId.
  const currentTemplateIdRef = useRef<string>("");
  // Marker for intentional account/client scope updates triggered by
  // rename/clone/save-draft flows, so the context-switch effect does not
  // wipe the just-saved template state.
  const pendingScopeSyncRef = useRef<{
    accountId: string;
    clientId: string;
  } | null>(null);

  // ========================================
  // Modal State
  // ========================================
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isSaveAsDraftModalOpen, setIsSaveAsDraftModalOpen] = useState(false);
  const [isHomeModalOpen, setIsHomeModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isImportInfoModalOpen, setIsImportInfoModalOpen] = useState(false);
  const [importInfoMessage, setImportInfoMessage] = useState("");
  const [activateAction, setActivateAction] = useState<
    "activate" | "deactivate"
  >("activate");
  const [isCloning, setIsCloning] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameErrorMessage, setRenameErrorMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [treeResetTrigger, setTreeResetTrigger] = useState(0);
  const [expandFolderName, setExpandFolderName] = useState<string | undefined>(
    undefined,
  );

  // ========================================
  // UI State
  // ========================================
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [isEditorLoading, setIsEditorLoading] = useState(false);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
  const [isRow1Collapsed, setIsRow1Collapsed] = useState(true);
  const [isRow2Collapsed, setIsRow2Collapsed] = useState(true);
  const [isHomeDisabled, setIsHomeDisabled] = useState(true);

  // Banner Notification State
  const [bannerVisible, setBannerVisible] = useState(false);
  const [bannerHeader, setBannerHeader] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerVariant, setBannerVariant] = useState<
    "info" | "success" | "warning" | "error"
  >("info");

  // ========================================
  // Template State
  // ========================================
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >(undefined);
  const [actionLabel, setActionLabel] = useState("Draft");
  const [templateUpdatedAt, setTemplateUpdatedAt] = useState<Date | null>(null);
  const [category, setCategory] = useState("Global");
  const [selectedPublishVersion, setSelectedPublishVersion] =
    useState<PublishOption>(DEFAULT_PUBLISH_VERSION);
  const [isChecked, setChecked] = useState(false);
  const [templatePublishVersions, setTemplatePublishVersions] = useState<
    PublishOption[]
  >([]);

  // ========================================
  // Action Button Disabled States
  // ========================================
  const [isPublishDisabled, setIsPublishDisabled] = useState(true);
  const [isRenameDisabled, setIsRenameDisabled] = useState(true);
  const [isCloneDisabled, setIsCloneDisabled] = useState(true);
  const [isMoveDisabled, setIsMoveDisabled] = useState(true);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [isSaveAsDraftDisabled, setIsSaveAsDraftDisabled] = useState(true);
  const [templateId, setTemplateId] = useState<string>("");

  // Draft info needed by PublishModal (populated after Save As Draft)
  const [draftInfo, setDraftInfo] = useState<{
    templateRowId: string;
    platformId: number;
    clientId: number;
  } | null>(null);

  // Info needed by ActivateVersionModal — templateRowId + the version's DB id to activate
  const [activateVersionInfo, setActivateVersionInfo] = useState<{
    templateRowId: string;
    templateVersionRowId: string;
  } | null>(null);

  // Draft version ID — sent back on subsequent Save As Draft calls so the
  // lambda updates the existing version row instead of inserting a duplicate
  const [draftVersionId, setDraftVersionId] = useState<string>("");

  // ========================================
  // Computed Values
  // ========================================
  const isActivePublishVersion =
    selectedPublishVersion?.label?.endsWith("_Active");

  // Checkbox should be disabled when Draft is selected (drafts can't be "Active")
  // or when the selected version is already the active published version,
  // or when user is TB_GENERAL_READONLY.
  const isCheckboxDisabled =
    selectedPublishVersion?.value === "Draft" ||
    !!isActivePublishVersion ||
    isReadOnlyUser(userRole);

  // Hide the version dropdown + active checkbox when the template has no published versions
  // (only a Draft entry exists — length === 1)
  const hasPublishedVersions = templatePublishVersions.length > 1;

  const syncContextForCategory = useCallback(
    (
      templateCategory: TemplateCategory,
      opts?: {
        accountId?: string;
        clientId?: string;
      },
    ): { nextAccountId: string; nextClientId: string } => {
      const requestedAccountId = opts?.accountId?.trim() || "";
      const requestedClientId = opts?.clientId?.trim() || "";

      const nextAccountId =
        templateCategory === "OPTUM"
          ? ""
          : requestedAccountId || accountId || "";
      const nextClientId =
        templateCategory === "CLIENT"
          ? requestedClientId || clientId || ""
          : "";
      const currentAccountId = accountId || "";
      const currentClientId = clientId || "";
      const hasScopeChange =
        nextAccountId !== currentAccountId || nextClientId !== currentClientId;

      if (!hasScopeChange) {
        pendingScopeSyncRef.current = null;
        return { nextAccountId, nextClientId };
      }

      pendingScopeSyncRef.current = {
        accountId: nextAccountId,
        clientId: nextClientId,
      };

      batch(() => {
        if (templateCategory === "OPTUM") {
          dispatch(setAccount({ account: "" }));
          dispatch(setClient({ client: "" }));
        } else if (templateCategory === "PLATFORM") {
          dispatch(setAccount({ account: nextAccountId }));
          dispatch(setClient({ client: "" }));
        } else if (templateCategory === "CLIENT") {
          dispatch(setAccount({ account: nextAccountId }));
          dispatch(setClient({ client: nextClientId }));
        }
      });

      return { nextAccountId, nextClientId };
    },
    [accountId, clientId, dispatch],
  );

  const applyTemplateMetaAccountContext = useCallback(
    (html: string) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const getMeta = (name: string) =>
        doc
          .querySelector(`meta[name="${name}"]`)
          ?.getAttribute("content")
          ?.trim() || "";

      const accountFromMeta = getMeta("account");
      const clientFromMeta = getMeta("client");
      const categoryFromMeta = normalizeTemplateCategory(
        getMeta("template-category"),
      );

      if (!categoryFromMeta) return;

      dispatch(
        setMetadataCategory({ category: mapAPICategoryToUI(categoryFromMeta) }),
      );

      // Push desired account/client context to AppLayout.
      // PLATFORM clears client, OPTUM clears both.
      if (categoryFromMeta === "OPTUM") {
        dispatch(setAccount({ account: "" }));
        dispatch(setClient({ client: "" }));
      } else if (categoryFromMeta === "PLATFORM") {
        dispatch(setAccount({ account: accountFromMeta }));
        dispatch(setClient({ client: "" }));
      } else if (categoryFromMeta === "CLIENT") {
        dispatch(setAccount({ account: accountFromMeta }));
        dispatch(setClient({ client: clientFromMeta }));
      }
    },
    [dispatch],
  );

  /**
   * Resets all template-specific state to a blank slate.
   * Shared across three call sites: account/client context switch,
   * library-template load, and unregistered-import fallback.
   */
  const resetTemplateState = useCallback(
    (opts: {
      /** Show the ActionBar (row 2). True for library/import, false for context switch. */
      showActionBar: boolean;
      /** Enable the Home button. True for library/import, false for context switch. */
      enableHome: boolean;
      /** Actual disabled value for the Save As Draft button. */
      isSaveAsDraftDisabled: boolean;
      /** Mark that a library template is now loaded (skips the editor readOnly guard). */
      markLibraryLoaded: boolean;
    }) => {
      pendingAutoSelectRef.current = null;
      pendingAutoSelectNameRef.current = null;
      dispatch(setFilename({ filename: "" }));
      setSelectedTemplateId("");
      setTemplateId("");
      setTemplatePublishVersions([]);
      setSelectedPublishVersion(DEFAULT_PUBLISH_VERSION);
      setChecked(false);
      setDraftInfo(null);
      setDraftVersionId("");
      setActivateVersionInfo(null);
      setActionLabel("Draft");
      setTemplateUpdatedAt(null);
      setIsRow1Collapsed(true);
      setIsRow2Collapsed(!opts.showActionBar);
      setIsHomeDisabled(!opts.enableHome);
      setIsSaveAsDraftDisabled(opts.isSaveAsDraftDisabled);
      setIsPublishDisabled(true);
      setIsRenameDisabled(true);
      setIsCloneDisabled(true);
      setIsMoveDisabled(true);
      setIsDeleteDisabled(true);
      isLibraryTemplateLoadedRef.current = opts.markLibraryLoaded;
    },
    [dispatch],
  );

  const resetUnregisteredImportState = useCallback(() => {
    dispatch(setFolderName({ folderName: "" }));
    currentTemplateIdRef.current = "";
    resetTemplateState({
      showActionBar: true,
      enableHome: true,
      isSaveAsDraftDisabled: isSaveDraftDisabledByRole,
      markLibraryLoaded: true,
    });
    editorRef.current?.setMetadata({
      templateId: "",
      templateName: "",
      userUuid: auth.user?.profile?.sub,
    });
  }, [
    dispatch,
    resetTemplateState,
    isSaveDraftDisabledByRole,
    auth.user?.profile?.sub,
  ]);

  const handleImportedTemplate = useCallback(
    async (payload: EditorImportPayload) => {
      if (!payload.hasTemplateTags) {
        // Generic HTML import — clear any previously loaded template state so
        // the PublishBar does not show stale templateId / templateName / version info.
        resetUnregisteredImportState();
        return;
      }

      const metadata = payload.metadata;
      const templateCategory = normalizeTemplateCategory(
        metadata.templateCategory,
      );
      if (!templateCategory) {
        return;
      }

      if (!canAutoRegisterCategory(userRole, templateCategory)) {
        // Role-conflict import should still behave like generic HTML import:
        // keep loaded content in editor and allow Save As Draft per role.
        resetUnregisteredImportState();
        setImportInfoMessage(
          getRoleConflictMessage(userRole, templateCategory),
        );
        setIsImportInfoModalOpen(true);
        return;
      }

      if (!auth.user?.access_token) {
        setImportInfoMessage(
          "Import Failed,          Your session has expired. Please log in again.,          error",
        );
        setIsImportInfoModalOpen(true);
        return;
      }

      setIsEditorLoading(true);

      try {
        const connector = new TemplatesDataConnector();
        const importResult: ImportTemplateOperationResponse =
          await connector.importTemplate(
            {
              templateId: metadata.templateId || "",
              templateName: metadata.templateName || "",
              templateFolder: metadata.templateFolder || "Drafts",
              templateCategory,
              account: metadata.account,
              client: metadata.client,
            },
            auth.user.access_token,
          );

        const targetVersion =
          importResult.versions?.find(
            (v) => v.templateVersionNumber === null,
          ) || importResult.versions?.[0];

        if (!targetVersion?.putPresignedUrl) {
          throw new Error(
            "The template record was saved but the upload link could not be generated. Please try again or contact support.",
          );
        }

        console.log( "Payload" + payload.cleanHtml) ;

        const s3Connector = new S3DataConnector();
        await s3Connector.uploadFile(
          payload.cleanHtml,
          targetVersion.putPresignedUrl,
          S3_CONSTANTS.PLATFORM_ID,
        );

        const uiCategory = mapAPICategoryToUI(importResult.templateCategory);
        const importFolderName = importResult.folderName || "Drafts";
        dispatch(setFilename({ filename: importResult.templateName }));
        dispatch(setFolderName({ folderName: importFolderName }));
        dispatch(setMetadataCategory({ category: uiCategory }));

        setCategory(uiCategory);
        setTemplateId(importResult.templateId);
        currentTemplateIdRef.current = importResult.templateId;
        pendingAutoSelectRef.current = importResult.templateId;
        pendingAutoSelectNameRef.current = importResult.templateName || null;
        setSelectedTemplateId(importResult.templateId);
        setExpandFolderName(importFolderName);

        setDraftInfo({
          templateRowId: String(importResult.templateRowId),
          platformId: importResult.platformId,
          clientId: importResult.clientId,
        });
        setDraftVersionId(String(targetVersion.templateVersionId));

        setActionLabel("Draft");
        setTemplateUpdatedAt(new Date());
        setIsRow1Collapsed(false);
        setIsRow2Collapsed(false);
        setIsHomeDisabled(false);

        setIsPublishDisabled(false);
        setIsSaveAsDraftDisabled(isSaveDraftDisabledByRole);
        setIsRenameDisabled(false);
        setIsCloneDisabled(false);
        setIsMoveDisabled(false);
        setIsDeleteDisabled(false);

        editorRef.current?.setMetadata({
          templateId: importResult.templateId,
          templateName: importResult.templateName,
          folderName: importFolderName,
          accountId: metadata.account || accountName || undefined,
          clientId: metadata.client || clientName || undefined,
          templateCategory: importResult.templateCategory,
          userUuid: auth.user?.profile?.sub,
        });

        if (auth.user?.access_token) {
          const { nextAccountId, nextClientId } = syncContextForCategory(
            templateCategory,
            {
              accountId:
                importResult.platformId !== null &&
                importResult.platformId !== undefined
                  ? String(importResult.platformId)
                  : undefined,
              clientId:
                importResult.clientId !== null &&
                importResult.clientId !== undefined
                  ? String(importResult.clientId)
                  : undefined,
            },
          );
          dispatch(
            templatesActions.getTemplatesRequested(
              buildRefreshParams(
                auth.user.access_token,
                nextAccountId,
                nextClientId,
              ),
            ),
          );
        }

        sendTemplateNotification(
          auth.user?.access_token,
     
          "Import Successful",
          `Template "${importResult.templateName || ""}" has been imported successfully!`,
          "success",
        
          (err) => logger.error("Import notification failed:", err),
        );
      } catch (error: unknown) {
        const fallback =
          "An unexpected error occurred during import. Please try again or contact support.";
        const message = (error as Error)?.message?.trim() || fallback;
        setImportInfoMessage("Import Failed " + message + " error");
        setIsImportInfoModalOpen(true);
        logger.error("Template import flow failed:", error);
      } finally {
        setIsEditorLoading(false);
      }
    },
    [
      accountName,
      auth.user?.access_token,
      auth.user?.profile?.sub,
      clientName,
      dispatch,
      isSaveDraftDisabledByRole,
      resetUnregisteredImportState,
      syncContextForCategory,
      userRole,
    ],
  );

  // ========================================
  // Effects
  // ========================================
  // Note: accountId and clientId are now managed by AppLayout
  // No need to clear them here on mount

  // Update category based on selected IDs
  useEffect(() => {
    if (accountId && clientId) {
      setCategory("Client");
    } else if (accountId) {
      setCategory("Account");
    } else {
      setCategory("Global");
    }
  }, [accountId, clientId]);

  // Show banner if Platform Admin has not selected Account and Client, or Client Admin has not selected Client
  useEffect(() => {
    const hasNoAccountId = !accountId || accountId === "" || accountId === "0";
    const hasNoClientId = !clientId || clientId === "" || clientId === "0";

    if (isPlatformAdmin(userRole) && hasNoAccountId && hasNoClientId) {
      setBannerHeader("Account Required");
      setBannerMessage(
        "Since your assigned role is TB_PLATFORM_ADMIN, you must select an Account for any appropriate action.",
      );
      setBannerVariant("error");
      setBannerVisible(true);
    } else if (isClientAdmin(userRole) && hasNoClientId) {
      setBannerHeader("Client Required");
      setBannerMessage(
        "Since your assigned role is TB_CLIENT_ADMIN, you must select a Client for any appropriate action.",
      );
      setBannerVariant("error");
      setBannerVisible(true);
    } else {
      setBannerVisible(false);
    }
  }, [userRole, accountId, clientId]);

  // When the account/client context switches, reset all template-specific state.
  // The tree will automatically reload with the new scope's templates, so showing
  // the previous template's content/state would be misleading.
  useEffect(() => {
    // Skip on initial mount — the first run just establishes the baseline context.
    if (!isContextInitializedRef.current) {
      isContextInitializedRef.current = true;
      return;
    }

    const pendingScopeSync = pendingScopeSyncRef.current;
    if (
      pendingScopeSync &&
      pendingScopeSync.accountId === (accountId || "") &&
      pendingScopeSync.clientId === (clientId || "")
    ) {
      pendingScopeSyncRef.current = null;
      return;
    }

    pendingAutoSelectRef.current = null;
    pendingAutoSelectNameRef.current = null;
    dispatch(setFolderName({ folderName: "" }));
    currentTemplateIdRef.current = "";
    resetTemplateState({
      showActionBar: false,
      enableHome: false,
      isSaveAsDraftDisabled: true,
      markLibraryLoaded: false,
    });
    if (editorRef.current) {
      editorRef.current.resetToDefault();
    }
  }, [accountId, clientId, dispatch, resetTemplateState]);

  useEffect(() => {
    if (!isPublishDisabled) {
      setActionLabel("Draft");
    }
  }, [isPublishDisabled]);

  // Enforce TB_GENERAL_READONLY restrictions: disable all buttons except Home
  useEffect(() => {
    if (isReadOnlyUser(userRole)) {
      const readOnlyStates = getReadOnlyButtonStates(userRole);
      setIsPublishDisabled(readOnlyStates.isPublishDisabled);
      setIsRenameDisabled(readOnlyStates.isRenameDisabled);
      setIsCloneDisabled(readOnlyStates.isCloneDisabled);
      setIsMoveDisabled(readOnlyStates.isMoveDisabled);
      setIsDeleteDisabled(readOnlyStates.isDeleteDisabled);
      setIsSaveAsDraftDisabled(readOnlyStates.isSaveAsDraftDisabled);
    } else if (isPlatformAdmin(userRole)) {
      // TB_PLATFORM_ADMIN: all action buttons require account selected
      const accountSelected = !!accountId;
      setIsRenameDisabled(!accountSelected);
      setIsCloneDisabled(!accountSelected);
      setIsSaveAsDraftDisabled(!accountSelected);
      if (!accountSelected) {
        setIsPublishDisabled(true);
        setIsDeleteDisabled(true);
      }
    } else if (isClientAdmin(userRole)) {
      // TB_CLIENT_ADMIN: all action buttons require both account and client selected
      const bothSelected = !!(accountId && clientId);
      setIsRenameDisabled(!bothSelected);
      setIsCloneDisabled(!bothSelected);
      if (!bothSelected) {
        setIsPublishDisabled(true);
        setIsDeleteDisabled(true);
      }
    }
  }, [userRole, selectedTemplateId, accountId, clientId]);

  // Watch for clone operation completing — send notification and refresh tree
  useEffect(() => {
    if (!isCloning || !lastOperation || lastOperation.name !== "cloneTemplate")
      return;
    if (templatesLoading) {
      cloneLoadingSeenRef.current = true;
      return;
    }
    // Stale lastOperation from a previous clone — wait until loading starts
    if (!cloneLoadingSeenRef.current) return;

    if (lastOperation.result && !templatesError) {
      sendTemplateNotification(
        auth.user?.access_token,
       
        "Template Cloned Successfully",
        `Template "${lastOperation.result.templateName || ""}" has been cloned successfully!`,
        category,

        (err) => logger.error("Clone notification failed:", err),
      );
      // Remember the cloned template's ID so the auto-select effect below can
      // find and open it once the tree refresh finishes.
      if (lastOperation.result.templateId) {
        pendingAutoSelectRef.current = lastOperation.result.templateId;
        pendingAutoSelectNameRef.current =
          lastOperation.result.templateName || null;
      }
      // Refresh the templates tree so the new clone appears
      if (auth.user?.access_token) {
        const cloneCategory =
          pendingCloneCategoryRef.current ?? mapUICategoryToAPI(category);
        const cloneUICategory = mapAPICategoryToUI(cloneCategory);
        dispatch(setMetadataCategory({ category: cloneUICategory }));
        setCategory(cloneUICategory);
        const { nextAccountId, nextClientId } =
          syncContextForCategory(cloneCategory);
        dispatch(
          templatesActions.getTemplatesRequested(
            buildRefreshParams(
              auth.user.access_token,
              nextAccountId,
              nextClientId,
            ),
          ),
        );
      }
    }
    setIsCloning(false);
    setIsCloneModalOpen(false);
  }, [
    isCloning,
    lastOperation,
    templatesLoading,
    templatesError,
    auth.user?.access_token,
    auth.user?.profile?.sub,
    category,
    dispatch,
    platformId,
    syncContextForCategory,
  ]);

  useEffect(() => {
    if (
      !isRenaming ||
      !lastOperation ||
      lastOperation.name !== "renameTemplate"
    )
      return;
    if (templatesLoading) {
      renameLoadingSeenRef.current = true;
      return;
    }
    if (!renameLoadingSeenRef.current) return;

    if (lastOperation.result && !templatesError) {
      sendTemplateNotification(
        auth.user?.access_token,
        
        "Template Renamed Successfully",
        `Template has been renamed to "${pendingRenameNameRef.current}" successfully!`,
        category,
        
        (err) => logger.error("Rename notification failed:", err),
      );

      // Update Redux metadata so the header reflects the new name/folder immediately.
      // The actual tree item will be corrected once getTemplatesRequested completes.
      dispatch(setFilename({ filename: pendingRenameNameRef.current }));
      dispatch(setFolderName({ folderName: pendingRenameFolderRef.current }));

      // Use the current template UUID for auto-select after the tree refreshes.
      if (selectedTemplateId) {
        pendingAutoSelectRef.current = selectedTemplateId;
        pendingAutoSelectNameRef.current = pendingRenameNameRef.current || null;
      }

      setIsEditModalOpen(false);
      setRenameErrorMessage("");

      // Refresh the tree so the renamed/moved template appears in its new location.
      if (auth.user?.access_token) {
        const renameCategory =
          pendingRenameCategoryRef.current ?? mapUICategoryToAPI(category);
        const renameUICategory = mapAPICategoryToUI(renameCategory);
        dispatch(setMetadataCategory({ category: renameUICategory }));
        setCategory(renameUICategory);
        const { nextAccountId, nextClientId } =
          syncContextForCategory(renameCategory);
        dispatch(
          templatesActions.getTemplatesRequested(
            buildRefreshParams(
              auth.user.access_token,
              nextAccountId,
              nextClientId,
            ),
          ),
        );
      }
    } else if (templatesError) {
      // Keep the modal open and show the error inline.
      setRenameErrorMessage(templatesError);
    }
    setIsRenaming(false);
  }, [
    isRenaming,
    lastOperation,
    templatesLoading,
    templatesError,
    auth.user?.access_token,
    auth.user?.profile?.sub,
    category,
    dispatch,
    platformId,
    selectedTemplateId,
    syncContextForCategory,
  ]);

  useEffect(() => {
    if (
      !isDeleting ||
      !lastOperation ||
      lastOperation.name !== "deleteTemplate"
    )
      return;
    if (templatesLoading) return;

    if (lastOperation.result && !templatesError) {
      sendTemplateNotification(
        auth.user?.access_token,
       
        "Template Deleted Successfully",
        `Template "${pendingDeleteNameRef.current}" has been deleted from folder "${pendingDeleteFolderRef.current}" successfully!`,
        mapUICategoryToAPI(category),
        
        (err) => logger.error("Delete notification failed:", err),
      );

      setDeleteErrorMessage("");
      setIsDeleteModalOpen(false);

      // Refresh the template list and clear selection
      if (auth.user?.access_token) {
        dispatch(
          templatesActions.getTemplatesRequested(
            buildRefreshParams(auth.user.access_token, accountId, clientId),
          ),
        );
      }

      dispatch(setFolderName({ folderName: "" }));
      currentTemplateIdRef.current = "";
      if (editorRef.current) {
        editorRef.current.loadHtml(DEFAULT_HTML);
      }
      resetTemplateState({
        showActionBar: false,
        enableHome: false,
        isSaveAsDraftDisabled: true,
        markLibraryLoaded: false,
      });
    } else if (templatesError) {
      // Keep the modal open and show the error inline.
      setDeleteErrorMessage(templatesError);
    }
    setIsDeleting(false);
  }, [isDeleting, lastOperation, templatesLoading, templatesError]);

  useEffect(() => {
    if (!selectedTemplateName && !isLibraryTemplateLoadedRef.current) {
      setIsSaveAsDraftDisabled(true);
    } else {
      setIsSaveAsDraftDisabled(isSaveDraftDisabledByRole);
    }
  }, [isSaveDraftDisabledByRole, selectedTemplateName, userRole]);

  // When the user picks a different version from the dropdown, load that version's HTML.
  // Depend on the primitive .value string (not the object reference) so the effect reliably
  // re-runs whenever the user selects a different version, even if the dropdown returns the
  // same object reference from the memoized items array.
  const selectedPublishVersionValue = selectedPublishVersion?.value;

  // When the user picks a different version from the dropdown, load that version's HTML.
  // This useEffect only handles the checkbox — HTML loading is done in handleVersionChangeByUser.
  useEffect(() => {
    // Always reset the manual "active" checkbox state when the version selection changes.
    // Without this, a previous activation leaves isChecked=true and blocks the next click.
    setChecked(false);
  }, [selectedPublishVersionValue]);

  // Auto-clear expandFolderName on the next animation frame after the tree has consumed it.
  // This allows the same folder to be re-expanded on subsequent operations without needing
  // a setTimeout at each call site.
  useEffect(() => {
    if (expandFolderName === undefined) return;
    const id = requestAnimationFrame(() => setExpandFolderName(undefined));
    return () => cancelAnimationFrame(id);
  }, [expandFolderName]);

  useEffect(() => {
    if (!pendingAutoSelectRef.current) return;
    if (templatesLoading) return;
    if (lastOperation?.name !== "getTemplates" || !lastOperation.result) return;

    const targetId = pendingAutoSelectRef.current;
    const expectedTemplateName =
      pendingAutoSelectNameRef.current?.trim().toLowerCase() || null;
    let matched = false;

    for (const folder of allTemplateFolders) {
      const template = folder.templates.find((t) => {
        if (t.templateId !== targetId) return false;
        if (!expectedTemplateName) return true;
        return t.templateName.trim().toLowerCase() === expectedTemplateName;
      });
      if (template) {
        matched = true;
        pendingAutoSelectRef.current = null; // clear before calling to avoid re-entry
        pendingAutoSelectNameRef.current = null;
        handleTemplateSelected(template, folder.folderName);
        break;
      }
    }

    if (matched) return;

    // Fall back to id-only selection if the expected name is not present yet.
    for (const folder of allTemplateFolders) {
      const template = folder.templates.find((t) => t.templateId === targetId);
      if (template) {
        pendingAutoSelectRef.current = null;
        pendingAutoSelectNameRef.current = null;
        handleTemplateSelected(template, folder.folderName);
        break;
      }
    }
  }, [
    allTemplateFolders,
    templatesLoading,
    lastOperation,
    handleTemplateSelected,
  ]);

  // ========================================
  // Event Handlers
  // ========================================
  const handleEmailTemplateLibraryClick = useCallback(() => {
    // When opening the library, collapse all folders and deselect any selection.
    // expandFolderName auto-clears to undefined on the next frame via useEffect.
    setExpandFolderName("");
    setSelectedTemplateId("");
    requestAnimationFrame(() => setSelectedTemplateId(undefined));

    // Set folder context to "Drafts" so Save As Draft defaults to Drafts folder
    dispatch(setFolderName({ folderName: "Drafts" }));

    setShowTemplateLibrary(true);
  }, [dispatch]);

  const handleLoadTemplateFromLibrary = useCallback(
    (html: string) => {
      setShowTemplateLibrary(false);
      // folderName is intentionally kept in Redux so Save As Draft pre-selects the last-used folder.
      keepLibraryEnabledRef.current = false;
      resetTemplateState({
        showActionBar: true,
        enableHome: true,
        isSaveAsDraftDisabled: isSaveDraftDisabledByRole,
        markLibraryLoaded: true,
      });
      if (editorRef.current) {
        editorRef.current.loadHtml(html);
      }
    },
    [resetTemplateState, isSaveDraftDisabledByRole],
  );

  const handleHomeClick = useCallback(() => {
    setIsHomeModalOpen(true);
  }, []);

  const handleHomeConfirm = useCallback(() => {
    dispatch(clearMetadata());
    resetAccountContext();
    currentTemplateIdRef.current = "";
    resetTemplateState({
      showActionBar: false,
      enableHome: false,
      isSaveAsDraftDisabled: true,
      markLibraryLoaded: false,
    });
    setExpandFolderName(undefined); // Reset folder expansion state
    keepLibraryEnabledRef.current = true;
    if (editorRef.current) {
      editorRef.current.resetToDefault();
    }
    setTreeResetTrigger((prev) => prev + 1);
    setIsHomeModalOpen(false);
  }, [dispatch, resetTemplateState]);

  const handleLoadTemplateVersion = useCallback(
    ({
      templateId,
      versionValue,
      s3Path,
    }: {
      templateId: string;
      versionValue: string;
      s3Path?: string;
    }) => {
      const token = auth.user?.access_token;
      const requestedVersionValue = versionValue || "Draft";

      // Guard: always load using the exact requested templateId + version.
      // Never infer version from selectedPublishVersion to avoid stale async reads.
      if (!templateId) {
        logger.warn("[Template Load] missing templateId for requested version");
        return;
      }

      if (!s3Path || !s3Path.startsWith("s3://") || !token) {
        logger.warn(
          "[Template Load] no valid s3Path or token | s3Path:",
          s3Path,
          "| hasToken:",
          !!token,
        );
        return;
      }

      const { html: cachedHtml } = getCachedTemplateHtml({
        templateHtmlById,
        templateId,
        versionValue: requestedVersionValue,
      });

      if (cachedHtml.trim()) {
        if (import.meta.env.DEV) {
          dispatch(
            templatesActions.templateHtmlServedFromCache({
              templateId,
              versionValue: requestedVersionValue,
              cacheKey: `${templateId}:${requestedVersionValue}`,
              s3Path,
            }),
          );
        }
        applyTemplateMetaAccountContext(cachedHtml);
        if (editorRef.current) editorRef.current.loadHtml(cachedHtml);
        setIsEditorLoading(false);
        return;
      }

      if (editorRef.current) editorRef.current.loadHtml("");
      setIsEditorLoading(true);
      pendingS3RequestRef.current = {
        s3Path,
        versionValue: requestedVersionValue,
        templateId,
      };
      dispatch(
        templatesActions.getTemplatePresignedUrlRequested({
          s3Path,
          token,
        }),
      );
    },
    [
      auth.user?.access_token,
      applyTemplateMetaAccountContext,
      dispatch,
      templateHtmlById,
    ],
  );

  function handleTemplateSelected(
    template: GetTemplateItem,
    folderName: string,
  ) {
    // Set the template name from the selected template
    dispatch(setFilename({ filename: template.templateName }));

    // Store the folder name in Redux for modal pre-selection
    dispatch(setFolderName({ folderName }));

    // A tree template is now loaded — clear the library-loaded flag
    isLibraryTemplateLoadedRef.current = false;

    // Map and store the category in Redux
    const categoryDisplay = mapAPICategoryToUI(template.templateCategory);
    dispatch(setMetadataCategory({ category: categoryDisplay }));

    // Store the selected templateId
    setSelectedTemplateId(template.templateId);

    // Get versions for this template from the selector (always includes Draft as first entry)
    const versions = versionsByTemplateId[template.templateId] || [];
    logger.debug("Template Publish Versions for dropdown:", versions);
    setTemplatePublishVersions(versions);

    // Pre-select dropdown based on template state:
    // - DRAFT state → show "Draft" (versions[0])
    // - PUBLISHED state → show whichever published version has isActive === "Y"
    //   (its label ends with "_Active"). Falls back to versions[1] if none found.
    const templateStateUpper = template.templateState.toUpperCase();
    const isTemplateDraft = templateStateUpper === "DRAFT";
    let defaultVersion: (typeof versions)[0];
    if (isTemplateDraft) {
      defaultVersion = versions[0];
    } else {
      const activeVersion = versions.find((v) => v.label?.endsWith("_Active"));
      defaultVersion = activeVersion ?? versions[1] ?? versions[0];
    }
    logger.debug("Pre-selected version in dropdown:", defaultVersion);
    setSelectedPublishVersion(defaultVersion);

    // Find the draft version (templateVersionNumber is null)
    const draftVersion = template.versions.find(
      (version) => version.templateVersionNumber === null,
    );

    if (templateStateUpper === "DRAFT") {
      setActionLabel("Draft");
      setTemplateUpdatedAt(new Date(template.updatedAt));
      setIsPublishDisabled(false);
    } else if (templateStateUpper === "PUBLISHED") {
      setActionLabel("Published");
      setTemplateUpdatedAt(new Date(template.updatedAt));
      setIsPublishDisabled(true);
    } else {
      setActionLabel("Draft");
      setTemplateUpdatedAt(null);
      setIsPublishDisabled(true);
    }
    setIsSaveAsDraftDisabled(isSaveDraftDisabledByRole);

    if (!draftVersion) {
      logger.error(
        `No draft version found for template ${template.templateName} (ID: ${template.templateId}) in folder ${folderName}`,
      );
      setDraftVersionId("");
    } else {
      setDraftVersionId(String(draftVersion.templateVersionId));
    }

    // Set draftInfo so the Publish modal has the DB row_id + platform/client scope
    setDraftInfo({
      templateRowId: String(template.rowId),
      platformId: accountId ? Number(accountId) : 0,
      clientId: clientId ? Number(clientId) : 0,
    });

    setTemplateId(template.templateId);
    currentTemplateIdRef.current = template.templateId;

    // Check if any version has "_Active" suffix - if so, disable Delete
    const hasActiveVersion = versions.some((v) => v.label.endsWith("_Active"));
    logger.debug("Has active version (Delete disabled?):", hasActiveVersion);
    // Sync template metadata into the GrapesJS editor for download/export
    editorRef.current?.setMetadata({
      templateId: template.templateId,
      templateName: template.templateName,
      folderName,
      accountId: accountName || undefined,
      clientId: clientName || undefined,
      templateCategory: template.templateCategory,
      userUuid: auth.user?.profile?.sub,
    });

    // Enable action buttons for template management
    setIsRenameDisabled(false);
    setIsCloneDisabled(false);
    setIsMoveDisabled(false);
    setIsDeleteDisabled(hasActiveVersion);

    // HTML: pick the s3Path matching the pre-selected dropdown version and dispatch
    // the presigned URL request so the editor loads the correct content.
    const versionValueToLoad = defaultVersion?.value ?? "Draft";
    const matchingVersion = template.versions.find((v) =>
      versionValueToLoad === "Draft"
        ? v.templateVersionNumber === null
        : v.templateVersionNumber ===
        Number(versionValueToLoad.replace("V", "")),
    );
    const s3PathToLoad = matchingVersion?.s3Path;
    logger.debug(
      "[Template Select] dropdown pre-select:",
      versionValueToLoad,
      "| versions:",
      template.versions.map((v) => ({
        num: v.templateVersionNumber,
        s3Path: v.s3Path,
      })),
    );
    logger.debug("[Template Select] using s3Path:", s3PathToLoad);
    handleLoadTemplateVersion({
      templateId: template.templateId,
      versionValue: versionValueToLoad,
      s3Path: s3PathToLoad,
    });

    // HTML loading is handled above via getTemplatePresignedUrlRequested.

    // Show row1 (PublishBar) and row2 (ActionBar) when child template is selected
    setIsRow1Collapsed(false);
    setIsRow2Collapsed(false);

    // Enable the Home button
    setIsHomeDisabled(false);
  }

  const handleTemplateLoading = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.loadHtml("");
    }
    setIsEditorLoading(true);
  }, []);

  // Called by EmailTemplate when the on-demand presigned URL for the selected
  // template has been resolved. Fetches the HTML and loads it into the editor.
  const handlePresignedUrlReady = useCallback(
    async (
      presignedUrl: string,
      requestContext?: { versionValue?: string; templateId?: string },
    ) => {
      if (!editorRef.current) {
        logger.warn("[handlePresignedUrlReady] editorRef is not ready");
        return;
      }

      // Use a dedicated abort controller so the selectedPublishVersionValue effect
      // cannot cancel this fetch by sharing fetchAbortControllerRef.
      if (presignedFetchAbortControllerRef.current) {
        presignedFetchAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      presignedFetchAbortControllerRef.current = controller;

      const templateId =
        requestContext?.templateId || currentTemplateIdRef.current;
      const requestVersionValue = requestContext?.versionValue ?? "Draft";

      try {
        const { html } = await getTemplateHtml({
          presignedUrl,
          templateHtmlById,
          templateId,
          versionValue: requestVersionValue,
          signal: controller.signal,
          onCacheWrite: (cacheKey, cacheHtml) => {
            dispatch(
              setTemplateHtmlById({ templateId: cacheKey, html: cacheHtml }),
            );
          },
        });

        applyTemplateMetaAccountContext(html);
        if (editorRef.current) editorRef.current.loadHtml(html);
        setIsEditorLoading(false);
      } catch (error) {
        if ((error as Error)?.name === "AbortError") return;
        logger.error(
          "[handlePresignedUrlReady] Failed to load template HTML:",
          error,
        );
        setIsEditorLoading(false);
      }
    },
    [
      applyTemplateMetaAccountContext,
      dispatch,
      templateHtmlById,
    ],
  );

  // Watch for the presigned URL that was requested when a template was clicked.
  // Once it arrives, fetch the HTML and load it into the editor.
  useEffect(() => {
    const pendingRequest = pendingS3RequestRef.current;
    if (!pendingRequest) return;
    const presignedUrl = presignedUrlByS3Path[pendingRequest.s3Path];
    if (presignedUrl) {
      pendingS3RequestRef.current = null;
      handlePresignedUrlReady(presignedUrl, {
        versionValue: pendingRequest.versionValue,
        templateId: pendingRequest.templateId,
      });
    }
  }, [presignedUrlByS3Path, handlePresignedUrlReady]);

  const handleDeleteConfirm = useCallback(() => {
    if (!auth.user?.access_token || !draftInfo?.templateRowId) return;

    const selectedCategoryApi = mapUICategoryToAPI(category);

    const deletePlatformId =
      selectedCategoryApi === "PLATFORM" || selectedCategoryApi === "CLIENT"
        ? accountId || undefined
        : undefined;
    const deletePlatformClientsId =
      selectedCategoryApi === "CLIENT" ? clientId || undefined : undefined;

    // Store template name for notification (API only returns a message string)
    pendingDeleteNameRef.current = selectedTemplateName;
    pendingDeleteFolderRef.current = folderName;

    dispatch(
      templatesActions.deleteTemplateRequested({
        token: auth.user.access_token,
        templateRowId: draftInfo.templateRowId,
        templateCategory: selectedCategoryApi,
        platformId: deletePlatformId,
        platformClientsId: deletePlatformClientsId,
      }),
    );
    setIsDeleting(true);
    setDeleteErrorMessage("");
  }, [
    auth.user,
    draftInfo,
    dispatch,
    category,
    accountId,
    clientId,
    selectedTemplateName,
    folderName,
  ]);

  // ========================================
  // Render
  // ========================================
  return (
    <div>
      {/* Banner Notification */}
      {bannerVisible && (
        <BannerNotification
          variant={bannerVariant}
          id={"notification-" + bannerVariant}
          dismissible={false}
          headerText={bannerHeader}
          announcementPriority="polite"
          headerAs="h3"
          onDismiss={() => setBannerVisible(false)}
        >
          <ContentWrapper>{bannerMessage}</ContentWrapper>
        </BannerNotification>
      )}

      {/* Template Library - conditionally rendered */}
      {showTemplateLibrary && (
        <div className="template-library-overlay">
          <TemplateLibraryPage
            onBack={() => setShowTemplateLibrary(false)}
            onLoadTemplate={handleLoadTemplateFromLibrary}
          />
        </div>
      )}

      {/* Main Template Builder - always mounted but hidden when library is shown */}
      <div
        className={`template-builder-main ${showTemplateLibrary ? "hidden" : ""}`}
      >
        <div className="grid grid-max-width">
          {/* Row 1: Full-width banner with toggle */}
          <div className={`row1 ${isRow1Collapsed ? "collapsed" : ""}`}>
            <PublishBar
              displayTemplateName={selectedTemplateName}
              textProps={textProps}
              templateId={templateId}
              actionLabel={actionLabel}
              formatDateTime={formatDateTime}
              templateUpdatedAt={templateUpdatedAt}
            />
            <button
              className="row1-toggle"
              onClick={() => setIsRow2Collapsed(!isRow2Collapsed)}
              aria-label={
                isRow2Collapsed ? "Expand section" : "Collapse section"
              }
            >
              {isRow2Collapsed ? (
                <ArrowDropDownIcon
                  style={{ fontSize: "40px", width: "40px", height: "40px" }}
                />
              ) : (
                <ArrowDropUpIcon
                  style={{ fontSize: "40px", width: "40px", height: "40px" }}
                />
              )}
            </button>
          </div>

          {/* Row 2: Action bar */}
          <div className={`row2 ${isRow2Collapsed ? "collapsed" : ""}`}>
            <ActionBar
              onPublish={() => setIsPublishModalOpen(true)}
              onRename={() => setIsEditModalOpen(true)}
              onClone={() => setIsCloneModalOpen(true)}
              onMove={() => setIsMoveModalOpen(true)}
              onDelete={() => {
                setDeleteErrorMessage("");
                setIsDeleteModalOpen(true);
              }}
              onSaveDraft={() => setIsSaveAsDraftModalOpen(true)}
              onHome={handleHomeClick}
              onActivate={() => {
                // Resolve the DB version ID for the currently selected published version.
                // Use the ref — never stale, even when selectedTemplateId is cleared by the
                // Save As Draft timeout (setSelectedTemplateId(undefined)).
                const effectiveTemplateId = currentTemplateIdRef.current;
                const versionId = effectiveTemplateId
                  ? versionIdMap[effectiveTemplateId]?.[
                  selectedPublishVersion?.value ?? ""
                  ]
                  : undefined;
                if (draftInfo?.templateRowId && versionId) {
                  setActivateVersionInfo({
                    templateRowId: draftInfo.templateRowId,
                    templateVersionRowId: String(versionId),
                  });
                  setChecked(true);
                  setActivateAction("activate");
                  setIsActivateModalOpen(true);
                }
              }}
              onDeactivate={() => {
                const effectiveTemplateId = currentTemplateIdRef.current;
                const rawValue =
                  selectedPublishVersion?.value?.replace("_Active", "") ?? "";
                const versionId = effectiveTemplateId
                  ? versionIdMap[effectiveTemplateId]?.[rawValue]
                  : undefined;
                if (draftInfo?.templateRowId && versionId) {
                  setActivateVersionInfo({
                    templateRowId: draftInfo.templateRowId,
                    templateVersionRowId: String(versionId),
                  });
                  setActivateAction("deactivate");
                  setIsActivateModalOpen(true);
                }
              }}
              canDeactivate={canDeactivateVersion(userRole)}
              publishVersion={templatePublishVersions}
              selectedPublishVersion={selectedPublishVersion}
              setSelectedPublishVersion={setSelectedPublishVersion}
              onVersionChangeByUser={(version) => {
                const versionValue = version.value;
                // Use the ref — never stale, even when selectedTemplateId is cleared by the
                // Save As Draft timeout (setSelectedTemplateId(undefined)).
                const effectiveTemplateId = currentTemplateIdRef.current;
                let matchingTemplate: GetTemplateItem | undefined;
                for (const folder of allTemplateFolders) {
                  matchingTemplate = folder.templates.find(
                    (t) => t.templateId === effectiveTemplateId,
                  );
                  if (matchingTemplate) break;
                }
                logger.debug(
                  "[Dropdown Change] user picked:",
                  versionValue,
                  "| templateId:",
                  effectiveTemplateId,
                  "| found:",
                  !!matchingTemplate,
                );
                if (!matchingTemplate) return;
                const ver = matchingTemplate.versions.find((v) =>
                  versionValue === "Draft"
                    ? v.templateVersionNumber === null
                    : v.templateVersionNumber ===
                    Number(versionValue.replace("V", "")),
                );
                const s3Path = ver?.s3Path;
                logger.debug("[Dropdown Change] s3Path:", s3Path);
                handleLoadTemplateVersion({
                  templateId: effectiveTemplateId,
                  versionValue,
                  s3Path,
                });
              }}
              isActivePublishVersion={isCheckboxDisabled}
              isDraftSelected={selectedPublishVersion?.value === "Draft"}
              isChecked={isChecked}
              isPublishDisabled={isPublishDisabled}
              isRenameDisabled={isRenameDisabled}
              isCloneDisabled={isCloneDisabled}
              isMoveDisabled={isMoveDisabled}
              // TODO: Delete feature is not yet ready — re-enable by replacing
              // `true` with `{isDeleteDisabled}` once the feature is complete.
              isDeleteDisabled={isDeleteDisabled}
              isDeleting={isDeleting}
              isSaveAsDraftDisabled={isSaveAsDraftDisabled}
              isDropdownDisabled={!hasPublishedVersions}
              isHomeDisabled={isHomeDisabled}
            />
          </div>

          {/* Row 3: Editor */}
          <div className="row-max-height">
            <TemplateWorkspace
              onEmailTemplateLibraryClick={handleEmailTemplateLibraryClick}
              isLeftCollapsed={isLeftCollapsed}
              onToggleLeft={() => setIsLeftCollapsed((prev) => !prev)}
              category={category}
              isTemplateDraft={actionLabel === "Draft"}
              isSaveAsDraftDisabled={isSaveAsDraftDisabled}
              onSaveAsDraft={() => setIsSaveAsDraftModalOpen(true)}
              onTemplateSelected={handleTemplateSelected}
              onTemplateLoading={handleTemplateLoading}
              resetTrigger={treeResetTrigger}
              selectedTemplateId={selectedTemplateId}
              expandFolderName={expandFolderName}
            />
            <div
              className={`col-80 ${isLeftCollapsed ? "is-expanded" : ""}`}
              style={{ position: "relative" }}
            >
              {isEditorLoading && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.75)",
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <LoadingIndicator
                      loading
                      loadingText="Loading template..."
                      accessibilityText="Loading template"
                    />
                  </div>
                </div>
              )}
              <Editor
                ref={editorRef}
                readOnly={
                  !selectedTemplateName && !isLibraryTemplateLoadedRef.current
                }
                onOpenLibrary={handleEmailTemplateLibraryClick}
                onTemplateImported={handleImportedTemplate}
              />
            </div>
          </div>
        </div>

        {isImportInfoModalOpen && (
          <Modal
            open={isImportInfoModalOpen}
            title="Import Information"
            aria-label="Import Information"
            onClose={() => setIsImportInfoModalOpen(false)}
          >
            <Modal.Body>
              <Text size="large">{importInfoMessage}</Text>
            </Modal.Body>
            <Modal.Actions>
              <Button
                variant="primary"
                onPress={() => setIsImportInfoModalOpen(false)}
              >
                OK
              </Button>
            </Modal.Actions>
          </Modal>
        )}

        <RenameModal
          open={isEditModalOpen}
          title="Rename Template"
          onClose={() => {
            setIsEditModalOpen(false);
            setRenameErrorMessage("");
          }}
          templateName={selectedTemplateName}
          templateId={draftInfo?.templateRowId}
          isLoading={isRenaming}
          errorMessage={renameErrorMessage}
          onConfirm={(formData) => {
            if (!auth.user?.access_token || !draftInfo?.templateRowId) return;
            const selectedCategoryApi = mapUICategoryToAPI(formData.category);

            const renamePlatformId =
              selectedCategoryApi === "PLATFORM" ||
                selectedCategoryApi === "CLIENT"
                ? accountId || undefined
                : undefined;
            const renamePlatformClientsId =
              selectedCategoryApi === "CLIENT"
                ? clientId || undefined
                : undefined;

            // Store new name for notification (API only returns a message string)
            pendingRenameNameRef.current = formData.templateName;
            pendingRenameFolderRef.current = formData.folderName;
            pendingRenameCategoryRef.current = selectedCategoryApi;

            // expandFolderName auto-clears to undefined on the next frame via useEffect.
            setExpandFolderName(formData.folderName);

            dispatch(
              templatesActions.renameTemplateRequested({
                token: auth.user.access_token,
                templateId: draftInfo.templateRowId,
                templateName: formData.templateName,
                templateFolder: formData.folderName,
                templateCategory: selectedCategoryApi,
                platformId: renamePlatformId,
                platformClientsId: renamePlatformClientsId,
              }),
            );
            renameLoadingSeenRef.current = false;
            setIsRenaming(true);
            setRenameErrorMessage("");
          }}
        />
        <DeleteModal
          open={isDeleteModalOpen}
          title="Delete Template"
          onClose={() => {
            setIsDeleteModalOpen(false);
            setDeleteErrorMessage("");
          }}
          templateName={selectedTemplateName}
          isLoading={isDeleting}
          errorMessage={deleteErrorMessage}
          publishedVersionCount={
            templatePublishVersions.filter((v) => v.value !== "Draft").length
          }
          onConfirm={handleDeleteConfirm}
        />
        <MoveModal
          open={isMoveModalOpen}
          title="Move Template"
          onClose={() => setIsMoveModalOpen(false)}
          templateName={selectedTemplateName}
          onConfirm={() => {
            /* TODO: implement move */
          }}
        />
        <CloneModal
          open={isCloneModalOpen}
          title="Clone Template"
          onClose={() => setIsCloneModalOpen(false)}
          isLoading={isCloning}
          onConfirm={(formData) => {
            if (!auth.user?.access_token || !draftInfo?.templateRowId) return;
            const selectedCategoryApi = mapUICategoryToAPI(formData.category);

            // Resolve the templateVersionId for the version currently shown in the editor.
            // selectedPublishVersion.value is "Draft", "V1", "V2", etc.
            // We look it up from the version-ID map so that cloning a published version
            // sends that published version's content, not always the Draft content.
            const currentVersionValue =
              selectedPublishVersion?.value ?? "Draft";
            const versionIdForClone = selectedTemplateId
              ? (versionIdMap[selectedTemplateId]?.[currentVersionValue] ??
                Number(draftVersionId))
              : Number(draftVersionId);

            if (!versionIdForClone) return;

            // expandFolderName auto-clears to undefined on the next frame via useEffect.
            setExpandFolderName(formData.folderName);

            cloneLoadingSeenRef.current = false;
            pendingCloneCategoryRef.current = selectedCategoryApi;
            dispatch(
              templatesActions.cloneTemplateRequested({
                token: auth.user.access_token,
                templateName: formData.templateName,
                templateFolder: formData.folderName,
                templateCategory: selectedCategoryApi,
                platformId:
                  selectedCategoryApi === "PLATFORM" ||
                    selectedCategoryApi === "CLIENT"
                    ? accountId || undefined
                    : undefined,
                platformClientsId:
                  selectedCategoryApi === "CLIENT"
                    ? clientId || undefined
                    : undefined,
                templateVersionId: String(versionIdForClone),
                templateId: draftInfo.templateRowId,
              }),
            );
            setIsCloning(true);
          }}
        />
        <PublishModal
          open={isPublishModalOpen}
          title="Publish Template"
          onClose={() => setIsPublishModalOpen(false)}
          templateName={selectedTemplateName}
          draftInfo={draftInfo}
          onConfirm={(result: PublishTemplateOperationResponse) => {
            // Update header status
            setActionLabel("Published");
            setTemplateUpdatedAt(new Date());

            // Build published versions dropdown from the publish response.
            // The most recently published version is always auto-activated by the backend,
            // so mark it with the _Active suffix so the checkbox is pre-checked and greyed out.
            const sortedVersions = (result.versions || [])
              .filter((v) => v.templateVersionNumber !== null)
              .sort(
                (a, b) =>
                  (b.templateVersionNumber || 0) -
                  (a.templateVersionNumber || 0),
              );

            const publishedVersions = sortedVersions.map((v, index) => {
              const vLabel = `V${v.templateVersionNumber}`;
              // The highest version number is the one just published — mark it Active
              const isLatest = index === 0;
              return {
                id: String(index + 2), // offset by 2: id=1 is reserved for Draft
                label: isLatest ? `${vLabel}_Active` : vLabel,
                value: vLabel,
              };
            });

            // Always prepend Draft as the first entry (id=1), followed by published versions
            const allVersions = [
              { id: "1", label: "Draft", value: "Draft" },
              ...publishedVersions,
            ];

            if (publishedVersions.length > 0) {
              setTemplatePublishVersions(allVersions);
              setSelectedPublishVersion(allVersions[1]); // pre-select the Active published version
            }

            // Disable Publish, enable Save As Draft
            setIsPublishDisabled(true);
            setIsSaveAsDraftDisabled(false);
            setIsDeleteDisabled(true);

            // Refresh tree
            if (auth.user?.access_token) {
              dispatch(
                templatesActions.getTemplatesRequested(
                  buildRefreshParams(
                    auth.user.access_token,
                    accountId,
                    clientId,
                  ),
                ),
              );
            }
          }}
        />
        <SaveAsDraftModal
          open={isSaveAsDraftModalOpen}
          title="Save Template As Draft"
          onClose={() => setIsSaveAsDraftModalOpen(false)}
          templateName={selectedTemplateName}
          editorRef={editorRef}
          templateVersionId={draftVersionId}
          templateId={templateId}
          onSuccess={(metadata) => {
            if (metadata) {
              setCategory(metadata.category);
              dispatch(setMetadataCategory({ category: metadata.category }));
              setTemplateId(metadata.templateId);
              currentTemplateIdRef.current = metadata.templateId;
              pendingAutoSelectRef.current = metadata.templateId;
              pendingAutoSelectNameRef.current = metadata.templateName || null;
              setSelectedTemplateId(metadata.templateId);
              dispatch(setFilename({ filename: metadata.templateName }));
              dispatch(setFolderName({ folderName: metadata.folderName }));

              const savedCategoryApi = mapUICategoryToAPI(metadata.category);
              const { nextAccountId, nextClientId } =
                syncContextForCategory(savedCategoryApi);

              // Sync updated metadata into the GrapesJS editor for download/export
              editorRef.current?.setMetadata({
                templateId: metadata.templateId,
                templateName: metadata.templateName,
                folderName: metadata.folderName || folderName,
                accountId: accountName || undefined,
                clientId: clientName || undefined,
                templateCategory: mapUICategoryToAPI(metadata.category),
                userUuid: auth.user?.profile?.sub,
              });

              setDraftInfo({
                templateRowId: metadata.templateRowId,
                platformId: metadata.platformId,
                clientId: metadata.clientId,
              });
              setDraftVersionId(String(metadata.templateVersionId));
              // Template now has a name — clear the library-loaded flag
              isLibraryTemplateLoadedRef.current = false;
              // After saving a draft, the template state becomes DRAFT — switch dropdown to "Draft"
              setSelectedPublishVersion((prev) => {
                const draftEntry = templatePublishVersions.find(
                  (v) => v.value === "Draft",
                );
                return draftEntry ?? prev;
              });

              // expandFolderName auto-clears to undefined on the next frame via useEffect.
              setExpandFolderName(metadata.folderName);

              if (auth.user?.access_token) {
                dispatch(
                  templatesActions.getTemplatesRequested(
                    buildRefreshParams(
                      auth.user.access_token,
                      nextAccountId,
                      nextClientId,
                    ),
                  ),
                );
              }
            }
            setIsRow1Collapsed(false);
            setIsPublishDisabled(false);
            setIsSaveAsDraftDisabled(false);
            setIsCloneDisabled(false);
            setIsRenameDisabled(false);
            setIsMoveDisabled(false);
            setIsDeleteDisabled(
              templatePublishVersions.some((v) => v.label.endsWith("_Active")),
            );
          }}
        />
        <HomeConfirmationModal
          title="Confirm To Reset Default Template"
          open={isHomeModalOpen}
          onClose={() => setIsHomeModalOpen(false)}
          onConfirm={handleHomeConfirm}
        />
        <ActivateVersionModal
          open={isActivateModalOpen}
          title={
            activateAction === "deactivate"
              ? "Deactivate Template Version"
              : "Activate Template Version"
          }
          action={activateAction}
          onClose={() => {
            setIsActivateModalOpen(false);
            setChecked(false);
          }}
          templateName={selectedTemplateName}
          versionLabel={selectedPublishVersion?.value?.replace("_Active", "")}
          activateInfo={activateVersionInfo}
          onConfirm={() => {
            if (activateAction === "deactivate") {
              // Remove _Active from the deactivated version in the dropdown
              setTemplatePublishVersions((prev) =>
                prev.map((v) => {
                  if (v.value === "Draft") return v;
                  const baseLabel = v.label.replace("_Active", "");
                  return { ...v, label: baseLabel };
                }),
              );

              setSelectedPublishVersion((prev) => {
                const baseLabel = (prev.label ?? "").replace("_Active", "");
                return { ...prev, label: baseLabel };
              });

              // No active version anymore — enable Delete
              setIsDeleteDisabled(false);
            } else {
              // Rebuild the version dropdown: mark the newly activated version with _Active,
              // clear _Active from all others, then refresh from the backend tree.
              const activeValue = selectedPublishVersion?.value; // e.g. "V2"

              setTemplatePublishVersions((prev) =>
                prev.map((v) => {
                  if (v.value === "Draft") return v;
                  const isNowActive = v.value === activeValue;
                  const baseLabel = v.label.replace("_Active", "");
                  return {
                    ...v,
                    label: isNowActive ? `${baseLabel}_Active` : baseLabel,
                  };
                }),
              );

              // Pre-select the now-active version so the checkbox shows greyed-out
              setSelectedPublishVersion((prev) => {
                const baseLabel = (prev.label ?? "").replace("_Active", "");
                return { ...prev, label: `${baseLabel}_Active` };
              });

              // Active version exists — disable Delete
              setIsDeleteDisabled(true);
            }

            // Refresh tree so the server-side isActive flags are reflected
            if (auth.user?.access_token) {
              dispatch(
                templatesActions.getTemplatesRequested(
                  buildRefreshParams(
                    auth.user.access_token,
                    accountId,
                    clientId,
                  ),
                ),
              );
            }
          }}
        />
      </div>
    </div>
  );
};

export default TemplateBuilder;
